import React, { useCallback, type RefObject } from 'react';

import { useDraggable, useDroppable, DragOverlay, useDragDropManager } from '@dnd-kit/react';
import mergeRefs from 'merge-refs';
import { useTranslation } from 'react-i18next';

import './Item.scss';
import {
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleItem,
  isFreeSlot,
} from '../../../../../types/schedule';
import { getColorForClass, getHhmmTimeString, getTournamentTimezone } from '../schedule.helpers';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { ClassFormatEnum, ClassMatchType } from '../../../../../types/enums';
import { Class, Court } from '../../../../../types/tournament';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction } from '../../../../../types/modal';
import {
  handleScheduleBulkSelectionClick,
  handleScheduleCtrlClick,
  preventShiftClickTextSelection,
} from '../../../../../features/schedule/scheduleBulkSelectionHandlers';
import { selectScheduleSelectedMatchIds } from '../../../../../features/schedule/scheduleSelectors';
import { SCHEDULE_DND_PERFORMANCE_MODE } from './performance';
import {
  useSchedulePlacementActionsContext,
  useSchedulePlacementSelectionContext,
} from './SchedulePlacementContext';
import type { ScheduleZoomTier } from '../NewScheduleLayout/ScheduleZoomContext';
import { getScheduleLaidOutRowHeightPx } from '../../../../../features/schedule/scheduleZoomBounds';
import type { GroupDragOverlayLayout } from './scheduleGroupDragOverlay';
import { MS_PER_MINUTE } from '../../../../../features/schedule/scheduleOverlap';

const FREE_SLOT_MINUTES = 5;

const pointerOnlyCollisionDetector = ({ dragOperation, droppable }: any) => {
  const pointerCoordinates = dragOperation.position.current;
  const shape = droppable.shape;

  if (!pointerCoordinates || !shape?.containsPoint(pointerCoordinates)) {
    return null;
  }

  return {
    id: droppable.id,
    value: 1,
    type: 2,
    priority: 3,
  };
};

const courtSurfaceCollisionDetector = ({ dragOperation, droppable }: any) => {
  const collision = pointerOnlyCollisionDetector({ dragOperation, droppable });

  return collision ? { ...collision, priority: 1 } : null;
};

interface ItemProps {
  id: string;
  item: IScheduleItem;
  scale?: number;
  width?: number;
  timeZone?: string;
  classObject?: Class;
  style?: React.CSSProperties;
}

export type MatchDragDisabledParams = {
  scheduleMutationBlocked: boolean;
  isScheduled: boolean;
  dragModifierBlocked: boolean;
  placementDragDisabled?: boolean;
};

export const isMatchDragDisabled = ({
  scheduleMutationBlocked,
  isScheduled,
  dragModifierBlocked,
  placementDragDisabled = false,
}: MatchDragDisabledParams) =>
  scheduleMutationBlocked || placementDragDisabled || (isScheduled && dragModifierBlocked);

const PLACEMENT_DND_BLOCK_KEYS = new Set([
  'Enter',
  ' ',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

export function shouldBlockPlacementDndKeyboard(
  key: string,
  isScheduled: boolean,
  placementDragDisabled: boolean,
  dragModifierBlocked: boolean
): boolean {
  if (!PLACEMENT_DND_BLOCK_KEYS.has(key)) return false;
  return placementDragDisabled || (isScheduled && dragModifierBlocked);
}

export type GridMatchPlacementBehavior = {
  swapContextActive: boolean;
  activePlacementMatchId: number | null;
  scheduleMutationBlocked: boolean;
  placementDragDisabled: boolean;
  dragModifierBlocked: boolean;
  onSelectForPlacement?: (matchId: number) => void;
  onSwapWithScheduledMatch?: (target: IMatchScheduleItem) => void | Promise<void>;
};

export type MatchItemProps = {
  id: string;
  item: IMatchScheduleItem;
  isScheduled: boolean;
  scale?: number;
  width?: number;
  classObject?: Class;
  style?: React.CSSProperties;
  onSelectForPlacement?: (matchId: number) => void;
  onUnscheduledDragStarted?: () => void;
  placementPressed?: boolean;
  placementSwapContextActive?: boolean;
  onSwapWithScheduledMatch?: (target: IMatchScheduleItem) => void | Promise<void>;
  activePlacementMatchId?: number | null;
  dimInvalidSwapTarget?: boolean;
  ctrlSwapSubtleDim?: boolean;
  swapKeyboardFocus?: boolean;
  conflictHighlight?: boolean;
  conflictSelected?: boolean;
  conflictFocused?: boolean;
  conflictFocusPulseGeneration?: number;
  bulkSelected?: boolean;
  shiftModifierHeld?: boolean;
  placementDragDisabled?: boolean;
  dragModifierBlocked?: boolean;
  scheduleMutationBlocked?: boolean;
  /** Grid path: read volatile placement state from ref (avoids context re-renders). */
  gridPlacementRef?: RefObject<GridMatchPlacementBehavior | null>;
  /** Grid path: top/height/width driven by parent CSS variables (stable across zoom). */
  cssZoomLayout?: boolean;
  zoomTier?: ScheduleZoomTier;
  groupDragGhostHidden?: boolean;
  groupDragOverlay?: GroupDragOverlayLayout | null;
  /** During bulk group drag, let pointer events pass through to the court drop zone. */
  groupDragDropPassthrough?: boolean;
};

export function matchItemPropsAreEqual(prev: MatchItemProps, next: MatchItemProps): boolean {
  const cssLayout = prev.cssZoomLayout && next.cssZoomLayout;
  if (
    prev.id !== next.id ||
    prev.isScheduled !== next.isScheduled ||
    (!cssLayout && prev.scale !== next.scale) ||
    (!cssLayout && prev.width !== next.width) ||
    (cssLayout && prev.zoomTier !== next.zoomTier) ||
    prev.cssZoomLayout !== next.cssZoomLayout ||
    prev.placementPressed !== next.placementPressed ||
    prev.placementDragDisabled !== next.placementDragDisabled ||
    prev.dragModifierBlocked !== next.dragModifierBlocked ||
    prev.scheduleMutationBlocked !== next.scheduleMutationBlocked ||
    prev.gridPlacementRef !== next.gridPlacementRef ||
    prev.dimInvalidSwapTarget !== next.dimInvalidSwapTarget ||
    prev.ctrlSwapSubtleDim !== next.ctrlSwapSubtleDim ||
    prev.swapKeyboardFocus !== next.swapKeyboardFocus ||
    prev.conflictHighlight !== next.conflictHighlight ||
    prev.conflictSelected !== next.conflictSelected ||
    prev.conflictFocused !== next.conflictFocused ||
    prev.conflictFocusPulseGeneration !== next.conflictFocusPulseGeneration ||
    prev.bulkSelected !== next.bulkSelected ||
    prev.shiftModifierHeld !== next.shiftModifierHeld ||
    prev.groupDragGhostHidden !== next.groupDragGhostHidden ||
    prev.groupDragOverlay !== next.groupDragOverlay ||
    prev.groupDragDropPassthrough !== next.groupDragDropPassthrough ||
    prev.onUnscheduledDragStarted !== next.onUnscheduledDragStarted ||
    prev.classObject !== next.classObject
  ) {
    return false;
  }

  const pi = prev.item;
  const ni = next.item;
  if (
    pi.id !== ni.id ||
    pi.classId !== ni.classId ||
    pi.matchId !== ni.matchId ||
    pi.sortKey !== ni.sortKey ||
    pi.scheduleDate !== ni.scheduleDate ||
    pi.duration !== ni.duration ||
    pi.court?.id !== ni.court?.id ||
    pi.homeTeam?.id !== ni.homeTeam?.id ||
    pi.awayTeam?.id !== ni.awayTeam?.id ||
    pi.homeTeam?.name !== ni.homeTeam?.name ||
    pi.awayTeam?.name !== ni.awayTeam?.name ||
    pi.homeTeamPlaceholder !== ni.homeTeamPlaceholder ||
    pi.awayTeamPlaceholder !== ni.awayTeamPlaceholder ||
    pi.tournamentGroup?.id !== ni.tournamentGroup?.id ||
    pi.tournamentGroup?.name !== ni.tournamentGroup?.name ||
    pi.type !== ni.type ||
    pi.bracket !== ni.bracket ||
    pi.isGold !== ni.isGold ||
    pi.isAdditional !== ni.isAdditional
  ) {
    return false;
  }

  if (prev.style?.left !== next.style?.left) {
    return false;
  }
  if (!cssLayout && prev.style?.top !== next.style?.top) {
    return false;
  }
  if (cssLayout) {
    if (prev.style?.top !== next.style?.top || prev.style?.height !== next.style?.height) {
      return false;
    }
  }

  return true;
}

export const Item = React.memo(function Item({
  id,
  item,
  scale = 10,
  width = 290,
  timeZone,
  classObject,
  style,
}: ItemProps) {
  if (!isFreeSlot(item)) {
    return (
      <MatchItem
        id={id}
        item={item}
        isScheduled={true}
        scale={scale}
        width={width}
        classObject={classObject}
        style={style}
      />
    );
  }
  return (
    <FreeSlotItem
      id={id}
      item={item}
      scale={scale}
      width={width}
      timeZone={timeZone}
      style={style}
    />
  );
});

function MatchItemComponent(props: MatchItemProps) {
  if (props.gridPlacementRef) {
    return <GridMatchItem {...props} gridPlacementRef={props.gridPlacementRef} />;
  }
  return <ContextMatchItem {...props} />;
}

function GridMatchItem({
  id,
  item,
  isScheduled,
  scale,
  width,
  classObject,
  style,
  cssZoomLayout = false,
  zoomTier = 'normal',
  placementPressed = false,
  dimInvalidSwapTarget = false,
  ctrlSwapSubtleDim = false,
  swapKeyboardFocus = false,
  conflictHighlight = false,
  conflictSelected = false,
  conflictFocused = false,
  conflictFocusPulseGeneration,
  bulkSelected = false,
  shiftModifierHeld = false,
  groupDragGhostHidden = false,
  groupDragOverlay = null,
  groupDragDropPassthrough = false,
  gridPlacementRef,
}: MatchItemProps & { gridPlacementRef: RefObject<GridMatchPlacementBehavior | null> }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const dndData = React.useMemo(() => ({ ...item, isScheduled }), [item, isScheduled]);

  const { isDragging, ref: draggableRef } = useDraggable({
    id,
    data: dndData,
    disabled: shiftModifierHeld,
  });

  const { ref: droppableRef } = useDroppable({
    id,
    data: dndData,
    disabled: !isScheduled || groupDragDropPassthrough,
    collisionDetector: pointerOnlyCollisionDetector,
    collisionPriority: 5,
    accept: (dropItem) => dropItem.data.isScheduled,
  });

  const duration = item.duration || 20;
  const currentScale = scale || 10;
  const currentWidth = width || 290;
  const isCompact = cssZoomLayout
    ? zoomTier !== 'normal'
    : currentScale <= 3 || currentWidth <= 80;
  const isSuperCompact = cssZoomLayout
    ? zoomTier === 'super-compact'
    : (currentScale <= 1 || currentWidth <= 60) && (currentScale <= 2 || currentWidth <= 40);
  const height = cssZoomLayout ? undefined : duration * currentScale;
  const itemWidth = cssZoomLayout ? undefined : currentWidth;
  const classColor = getColorForClass(+item.classId);
  const className = classObject?.name || 'Unknown Class';
  const tournamentGroupColor = item.tournamentGroup
    ? getColorForClass(+item.tournamentGroup?.id)
    : '';

  const itemClassName = `item draggable schedule-match ${isCompact ? 'is-compact' : ''} ${
    SCHEDULE_DND_PERFORMANCE_MODE ? 'performance-mode' : ''
  } ${dimInvalidSwapTarget ? 'item-swap-invalid-target' : ''} ${
    ctrlSwapSubtleDim ? 'item-swap-ctrl-subtle-dim' : ''
  } ${swapKeyboardFocus ? 'item-swap-keyboard-focus' : ''} ${
    conflictHighlight ? 'schedule-match--conflict' : ''
  } ${conflictSelected ? 'schedule-match--conflict-selected' : ''} ${
    conflictFocused ? 'schedule-match--conflict-focus' : ''
  } ${bulkSelected || placementPressed ? 'schedule-match--selected' : ''} ${
    groupDragGhostHidden ? 'schedule-match--group-drag-ghost' : ''
  }`;

  const handleMatchClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const placement = gridPlacementRef.current;
      if (!placement || placement.scheduleMutationBlocked) return;

      if (
        handleScheduleBulkSelectionClick(e, item.id, dispatch, {
          shiftStartsMarquee: true,
          singleClickStartsPlacement: true,
        })
      ) {
        return;
      }

      if (
        placement.swapContextActive &&
        isScheduled &&
        placement.onSwapWithScheduledMatch &&
        placement.activePlacementMatchId != null &&
        item.id !== placement.activePlacementMatchId
      ) {
        e.preventDefault();
        if (dimInvalidSwapTarget) return;
        await placement.onSwapWithScheduledMatch(item);
        return;
      }

      if (
        (e.ctrlKey || e.metaKey) &&
        isScheduled &&
        placement.swapContextActive &&
        placement.activePlacementMatchId === item.id
      ) {
        e.preventDefault();
        return;
      }

      if (handleScheduleCtrlClick(e, item.id, selectedMatchIds, dispatch, isScheduled, t)) {
        return;
      }

      placement.onSelectForPlacement?.(item.id);
    },
    [dimInvalidSwapTarget, dispatch, gridPlacementRef, isScheduled, item, selectedMatchIds, t]
  );

  const handlePointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    preventShiftClickTextSelection(e);
    if (e.shiftKey) {
      return;
    }
    const placement = gridPlacementRef.current;
    if (
      placement &&
      isMatchDragDisabled({
        scheduleMutationBlocked: placement.scheduleMutationBlocked,
        isScheduled,
        dragModifierBlocked: placement.dragModifierBlocked,
        placementDragDisabled: placement.placementDragDisabled,
      })
    ) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation?.();
      return;
    }
    if (!isScheduled || !(e.ctrlKey || e.metaKey)) return;
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
  };

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const placement = gridPlacementRef.current;
    if (!placement) return;
    if (
      shouldBlockPlacementDndKeyboard(
        e.key,
        isScheduled,
        placement.placementDragDisabled,
        placement.dragModifierBlocked
      )
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      openModal({
        currentModal: ModalAction.EDIT_MATCH_SCHEDULE,
        data: { match: item },
        modalOpen: true,
      })
    );
  };

  return (
    <>
    <div
      /* @ts-ignore - this works */
      ref={mergeRefs(draggableRef, droppableRef)}
      className={itemClassName}
      data-match-id={item.id}
      data-dragging={isDragging}
      data-schedule-place-anchor="true"
      data-schedule-swap-focus={swapKeyboardFocus ? item.id : undefined}
      data-conflict-pulse={
        conflictFocused && conflictFocusPulseGeneration != null
          ? conflictFocusPulseGeneration
          : undefined
      }
      aria-pressed={placementPressed}
      onPointerDownCapture={handlePointerDownCapture}
      onKeyDownCapture={handleKeyDownCapture}
      onClick={handleMatchClick}
      onContextMenu={handleContextMenu}
      title={t(
        'creativeDetail-scheduleMatchGridSelectForPlacement',
        'Click to select this match for placement, then click a court time. Right-click to edit.'
      )}
      style={
        {
          ...(height != null
            ? { height: `${height}px`, minHeight: `${height}px` }
            : { minHeight: style?.height }),
          ...(itemWidth != null ? { width: `${itemWidth}px` } : {}),
          ...(!cssZoomLayout ? { '--scale-y': currentScale } : {}),
          ...style,
        } as React.CSSProperties
      }
    >
      <div className="item-content">
        <div className="item-content-header" style={{ backgroundColor: classColor }}>
          {!isSuperCompact && <div className="item-content-header-class">{className}</div>}
          {!isSuperCompact && item.tournamentGroup && (
            <div
              className="item-content-header-group"
              style={{ backgroundColor: tournamentGroupColor }}
            >
              {item.tournamentGroup?.name}
            </div>
          )}
          {!isCompact && <div className="item-content-header-classMatch">{item.sortKey}</div>}
        </div>
        {!isCompact && (
          <div className="item-content-body">
            <div className="item-content-body-row">
              <RoundType item={item} classObject={classObject} />
              <div className="item-content-body-matchNumber">{item.matchId}</div>
            </div>
            <div className="item-content-body-teams">
              <div className="item-content-body-teams-team">
                {item.homeTeam?.name || item.homeTeamPlaceholder || 'TBD'}
              </div>
              <div className="item-content-body-teams-vs">vs</div>
              <div className="item-content-body-teams-team">
                {item.awayTeam?.name || item.awayTeamPlaceholder || 'TBD'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {isDragging && groupDragOverlay && (
      <DragOverlay>
        <div
          className="schedule-group-drag-overlay"
          style={{
            position: 'relative',
            width: groupDragOverlay.width,
            height: groupDragOverlay.height,
            pointerEvents: 'none',
          }}
        >
          {groupDragOverlay.items.map(({ match, left, top, width, height: itemHeight }) => {
            const previewColor = getColorForClass(+match.classId);
            const label = match.matchId || match.sortKey || className;
            return (
              <div
                key={match.id}
                className="item item-drag-preview performance-mode schedule-group-drag-overlay__item"
                style={{
                  position: 'absolute',
                  left,
                  top,
                  width,
                  height: itemHeight,
                  backgroundColor: previewColor,
                }}
              >
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      </DragOverlay>
    )}
    </>
  );
}

function ContextMatchItem({
  id,
  item,
  isScheduled,
  scale,
  width,
  classObject,
  style,
  onSelectForPlacement: onSelectForPlacementProp,
  onUnscheduledDragStarted,
  placementPressed: placementPressedProp,
  placementSwapContextActive: placementSwapContextActiveProp,
  onSwapWithScheduledMatch: onSwapWithScheduledMatchProp,
  activePlacementMatchId: activePlacementMatchIdProp = null,
  dimInvalidSwapTarget = false,
  ctrlSwapSubtleDim = false,
  swapKeyboardFocus = false,
  conflictHighlight = false,
  conflictSelected = false,
  conflictFocused = false,
  conflictFocusPulseGeneration,
  bulkSelected = false,
  placementDragDisabled: placementDragDisabledProp,
  dragModifierBlocked: dragModifierBlockedProp,
  scheduleMutationBlocked: scheduleMutationBlockedProp,
}: MatchItemProps) {
  const placementActionsCtx = useSchedulePlacementActionsContext();
  const placementSelectionCtx = useSchedulePlacementSelectionContext();
  const placementSwapContextActive =
    placementSwapContextActiveProp ?? placementActionsCtx?.swapContextActive ?? false;
  const onSwapWithScheduledMatch =
    onSwapWithScheduledMatchProp ?? placementActionsCtx?.onSwapWithScheduledMatch;
  const activePlacementMatchId =
    activePlacementMatchIdProp ?? placementActionsCtx?.activePlacementMatchId ?? null;
  const onSelectForPlacement =
    onSelectForPlacementProp ?? placementActionsCtx?.onSelectGridMatchForPlacement;
  const placementPressed =
    placementPressedProp ??
    (placementSelectionCtx != null &&
      placementSelectionCtx.selectedGridMatchId != null &&
      placementSelectionCtx.selectedGridMatchId === item.id);
  const placementDragDisabled =
    placementDragDisabledProp ?? placementActionsCtx?.placementDragDisabled ?? false;
  const dragModifierBlocked =
    dragModifierBlockedProp ?? placementActionsCtx?.dragModifierBlocked ?? false;
  const scheduleMutationBlocked =
    scheduleMutationBlockedProp ?? placementActionsCtx?.scheduleMutationBlocked ?? false;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const dndData = React.useMemo(() => ({ ...item, isScheduled }), [item, isScheduled]);

  const dragDisabled = isMatchDragDisabled({
    scheduleMutationBlocked,
    isScheduled,
    dragModifierBlocked,
    placementDragDisabled,
  });
  const { isDragging, ref: draggableRef } = useDraggable({
    id,
    data: dndData,
    disabled: dragDisabled,
  });
  const dragDropManager = useDragDropManager();

  React.useEffect(() => {
    if (!dragDisabled || !isDragging || !dragDropManager) return;
    //@ts-ignore
    dragDropManager.actions.stop({ canceled: true });
  }, [dragDisabled, isDragging, dragDropManager]);

  React.useEffect(() => {
    if (isDragging && !isScheduled && onUnscheduledDragStarted) {
      onUnscheduledDragStarted();
    }
  }, [isDragging, isScheduled, onUnscheduledDragStarted]);

  const { ref: droppableRef } = useDroppable({
    id,
    data: dndData,
    disabled: !isScheduled,
    collisionDetector: pointerOnlyCollisionDetector,
    collisionPriority: 5,
    // We don't allow dropping unscheduled items on a scheduled item (since we cant swap)
    accept: (dropItem) => dropItem.data.isScheduled,
  });

  const resolvedClassObject = classObject;

  const duration = item.duration || 20;
  const height = duration * (scale || 10);

  const currentScale = scale || 10;
  const currentWidth = width || 290;
  const isCompact = currentScale <= 3 || currentWidth <= 80;
  const isSuperCompact =
    (currentScale <= 1 || currentWidth <= 60) && (currentScale <= 2 || currentWidth <= 40);

  const classColor = getColorForClass(+item.classId);
  const className = resolvedClassObject?.name || 'Unknown Class';
  const tournamentGroupColor = item.tournamentGroup
    ? getColorForClass(+item.tournamentGroup?.id)
    : '';

  const itemClassName = `item draggable schedule-match ${isCompact ? 'is-compact' : ''} ${
    SCHEDULE_DND_PERFORMANCE_MODE ? 'performance-mode' : ''
  } ${dimInvalidSwapTarget ? 'item-swap-invalid-target' : ''} ${
    ctrlSwapSubtleDim ? 'item-swap-ctrl-subtle-dim' : ''
  } ${swapKeyboardFocus ? 'item-swap-keyboard-focus' : ''} ${
    conflictHighlight ? 'schedule-match--conflict' : ''
  } ${conflictSelected ? 'schedule-match--conflict-selected' : ''} ${
    conflictFocused ? 'schedule-match--conflict-focus' : ''
  } ${bulkSelected || placementPressed ? 'schedule-match--selected' : ''}`;

  const renderItemContent = () => {
    return (
      <>
        <div className="item-content">
          <div className="item-content-header" style={{ backgroundColor: classColor }}>
            {!isSuperCompact && <div className="item-content-header-class">{className}</div>}
            {!isSuperCompact && item.tournamentGroup && (
              <div
                className="item-content-header-group"
                style={{ backgroundColor: tournamentGroupColor }}
              >
                {item.tournamentGroup?.name}
              </div>
            )}
            {!isCompact && <div className="item-content-header-classMatch">{item.sortKey}</div>}
          </div>
          {!isCompact && (
            <div className="item-content-body">
              <div className="item-content-body-row">
                <RoundType item={item} classObject={resolvedClassObject} />
                <div className="item-content-body-matchNumber">{item.matchId}</div>
              </div>
              <div className="item-content-body-teams">
                <div className="item-content-body-teams-team">
                  {item.homeTeam?.name || item.homeTeamPlaceholder || 'TBD'}
                </div>
                <div className="item-content-body-teams-vs">vs</div>
                <div className="item-content-body-teams-team">
                  {item.awayTeam?.name || item.awayTeamPlaceholder || 'TBD'}
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const handleMatchClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (scheduleMutationBlocked) {
      return;
    }

    if (
      handleScheduleBulkSelectionClick(e, item.id, dispatch, {
        singleClickStartsPlacement: Boolean(onSelectForPlacement),
      })
    ) {
      return;
    }

    if (
      placementSwapContextActive &&
      isScheduled &&
      onSwapWithScheduledMatch &&
      activePlacementMatchId != null &&
      item.id !== activePlacementMatchId
    ) {
      e.preventDefault();
      if (dimInvalidSwapTarget) return;
      await onSwapWithScheduledMatch(item);
      return;
    }

    // Avoid quick-unschedule while this card is the selected placement match (Ctrl/Cmd would otherwise unschedule).
    if (
      (e.ctrlKey || e.metaKey) &&
      isScheduled &&
      placementSwapContextActive &&
      activePlacementMatchId === item.id
    ) {
      e.preventDefault();
      return;
    }

    if (handleScheduleCtrlClick(e, item.id, selectedMatchIds, dispatch, isScheduled, t)) {
      return;
    }

    if (onSelectForPlacement) {
      onSelectForPlacement(item.id);
      return;
    }
  };

  const handlePointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    preventShiftClickTextSelection(e);
    if (!isScheduled || !(e.ctrlKey || e.metaKey)) return;
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      openModal({
        currentModal: ModalAction.EDIT_MATCH_SCHEDULE,
        data: { match: item },
        modalOpen: true,
      })
    );
  };

  return (
    <>
      <div
        /* @ts-ignore - this works */
        ref={mergeRefs(draggableRef, droppableRef)}
        className={itemClassName}
        data-match-id={item.id}
        data-dragging={isDragging}
        data-schedule-place-anchor="true"
        data-schedule-swap-focus={swapKeyboardFocus ? item.id : undefined}
        data-conflict-pulse={
          conflictFocused && conflictFocusPulseGeneration != null
            ? conflictFocusPulseGeneration
            : undefined
        }
        aria-pressed={placementPressed === undefined ? undefined : placementPressed}
        onPointerDownCapture={handlePointerDownCapture}
        onClick={handleMatchClick}
        onContextMenu={handleContextMenu}
        title={
          isScheduled
            ? placementSwapContextActive && onSwapWithScheduledMatch
              ? t(
                  'creativeDetail-scheduleMatchTitlePlacementCtrlSwap',
                  'Click another match to swap with the one you are placing. Hold Ctrl (Cmd on Mac) to preview valid targets.'
                )
              : onSelectForPlacement
              ? t(
                  'creativeDetail-scheduleMatchGridSelectForPlacement',
                  'Click to select this match for placement, then click a court time. Right-click to edit.'
                )
              : t(
                  'creativeDetail-scheduleMatchTitleQuickUnschedule',
                  'Right-click for match details. Ctrl+Click (Cmd on Mac) to quick unschedule.'
                )
            : onSelectForPlacement
            ? t(
                'creativeDetail-scheduleUnscheduledCardTitleHint',
                'Click to select for placement on the grid. Right-click to edit.'
              )
            : undefined
        }
        style={
          {
            height: `${height}px`,
            minHeight: `${height}px`,
            width: `${currentWidth}px`,
            '--scale-y': currentScale,
            ...style,
          } as React.CSSProperties
        }
      >
        {renderItemContent()}
      </div>
      {isDragging && (
        <DragOverlay>
          {SCHEDULE_DND_PERFORMANCE_MODE ? (
            <div
              className="item item-drag-preview performance-mode"
              style={{
                backgroundColor: classColor,
                height: `${height}px`,
                width: `${currentWidth}px`,
              }}
            >
              <span>{item.matchId || item.sortKey || className}</span>
            </div>
          ) : (
            <div
              className={`item ${isCompact ? 'is-compact' : ''}`}
              data-dragging={isDragging}
              style={{ height: `${height}px`, width: `${currentWidth}px` }}
            >
              {renderItemContent()}
            </div>
          )}
        </DragOverlay>
      )}
    </>
  );
}

export const MatchItem = React.memo(MatchItemComponent, matchItemPropsAreEqual);

export const FreeSlotItem = React.memo(function FreeSlotItem({
  item,
  scale,
  width,
  timeZone,
  showLabel = true,
  style,
}: {
  id?: string;
  item: IFreeSlotItem;
  scale?: number;
  width?: number;
  timeZone?: string;
  showLabel?: boolean;
  style?: React.CSSProperties;
}) {
  const tournamentInfo = useAppSelector((state) =>
    timeZone ? null : state.schedule.tournamentInfo
  );
  const resolvedTimeZone =
    timeZone || getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);

  const duration = item.duration || 5;
  const height = duration * (scale || 10);

  const timeString = showLabel ? getHhmmTimeString(item.scheduleDate, resolvedTimeZone) : '';

  return (
    <div
      className="item item-free-slot"
      style={{
        height: `${height}px`,
        minHeight: `${height}px`,
        width: `${width || 290}px`,
        borderBottom: '1px solid #ddd',
        position: 'relative',
        ...style,
      }}
    >
      {showLabel && (
        <span
          style={{
            fontSize: '10px',
            color: '#aaa',
            position: 'absolute',
            top: '2px',
            left: '6px',
            pointerEvents: 'none',
          }}
        >
          {timeString} - {item.court.name}
        </span>
      )}
    </div>
  );
});

type CourtDropZoneProps = {
  id: string;
  court: Court;
  day: number;
  dayStartMs: number;
  dayEndMs: number;
  scale: number;
  style?: React.CSSProperties;
  /** When true, empty court surface accepts clicks to place a pending unscheduled match */
  placeModeActive?: boolean;
  onPlaceClick?: (freeSlot: IFreeSlotItem) => void;
  courtLabel?: string;
};

function courtDropZonePropsAreEqual(
  prev: CourtDropZoneProps,
  next: CourtDropZoneProps
): boolean {
  return (
    prev.id === next.id &&
    prev.court.id === next.court.id &&
    prev.court.name === next.court.name &&
    prev.day === next.day &&
    prev.dayStartMs === next.dayStartMs &&
    prev.dayEndMs === next.dayEndMs &&
    prev.scale === next.scale &&
    prev.placeModeActive === next.placeModeActive &&
    prev.onPlaceClick === next.onPlaceClick &&
    prev.courtLabel === next.courtLabel &&
    prev.style?.top === next.style?.top &&
    prev.style?.left === next.style?.left &&
    prev.style?.width === next.style?.width &&
    prev.style?.height === next.style?.height &&
    prev.style?.position === next.style?.position
  );
}

function CourtDropZoneComponent({
  id,
  court,
  day,
  dayStartMs,
  dayEndMs,
  scale,
  style,
  placeModeActive,
  onPlaceClick,
  courtLabel,
}: CourtDropZoneProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const droppableData = React.useMemo(
    () => ({
      isCourtDropZone: true,
      isFreeSlot: true,
      isScheduled: true,
      day,
      court,
      dayStartMs,
      dayEndMs,
      scale,
    }),
    [court, day, dayEndMs, dayStartMs, scale]
  );
  const { ref: droppableRef } = useDroppable({
    id,
    data: droppableData,
    collisionDetector: courtSurfaceCollisionDetector,
    collisionPriority: 1,
  });

  const buildSlotFromLocalY = (localY: number) => {
    const rowHeight = getScheduleLaidOutRowHeightPx(scale);
    const maxSlotIndex = Math.max(
      0,
      Math.ceil((dayEndMs - dayStartMs) / (FREE_SLOT_MINUTES * MS_PER_MINUTE)) - 1
    );
    const slotIndex = Math.max(0, Math.min(maxSlotIndex, Math.floor(localY / rowHeight)));
    const scheduleDateMs = dayStartMs + slotIndex * FREE_SLOT_MINUTES * MS_PER_MINUTE;
    return {
      isFreeSlot: true,
      isScheduled: true,
      day,
      court,
      scheduleDate: new Date(scheduleDateMs).toISOString(),
      duration: FREE_SLOT_MINUTES,
    } as IFreeSlotItem;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placeModeActive || !onPlaceClick) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const localY = e.clientY - rect.top;
    onPlaceClick(buildSlotFromLocalY(localY));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!placeModeActive || !onPlaceClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onPlaceClick(buildSlotFromLocalY(0));
    }
  };

  const rootClassName = [
    'court-drop-zone',
    placeModeActive ? 'court-drop-zone--place-mode' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={droppableRef}
      role={placeModeActive ? 'button' : undefined}
      tabIndex={placeModeActive ? 0 : undefined}
      aria-label={
        placeModeActive
          ? courtLabel ||
            `${court.name}. ${t(
              prefix + 'scheduleCourtDropZonePlaceAriaFallback',
              'Click a time on this court to place, or use arrow keys and Enter on the grid.'
            )}`
          : undefined
      }
      data-schedule-place-anchor="true"
      className={rootClassName}
      style={style}
      onClick={placeModeActive ? handleClick : undefined}
      onKeyDown={placeModeActive ? handleKeyDown : undefined}
    />
  );
}

export const CourtDropZone = React.memo(CourtDropZoneComponent, courtDropZonePropsAreEqual);

const RoundType = ({ item, classObject }: { item: IMatchScheduleItem; classObject?: Class }) => {
  const isGroupFormat = classObject?.format === ClassFormatEnum.GROUP;
  const isFinal = item.type === ClassMatchType.FINAL;
  const isWinnersBracket = item.bracket === 1;

  return (
    <div
      className="item-content-body-round"
      style={{
        backgroundColor: isFinal ? (item.isGold ? 'gold' : 'orange') : '',
      }}
    >
      {isFinal
        ? item.isGold
          ? item.isAdditional
            ? 'Add. Final'
            : 'Final'
          : isGroupFormat
          ? 'Bronze'
          : 'Losers Final'
        : 'Round ' +
          (isGroupFormat
            ? item.type + (item.tournamentGroup ? ' ' + item.tournamentGroup.name : '')
            : isWinnersBracket
            ? 'W' + item.type
            : 'L' + item.type)}
    </div>
  );
};
